import { Box, Typography } from "@mui/material";
import { useState } from "react";
import useColors from "../../../../../api/hooks/useColors";
import SearchFilter from "../../../../../components/filters/SearchFilter";
import SearchInput from "../../../../../components/filters/SearchInput";
import {
  isRawDataField,
  RAW_DATA_DELIMITER,
} from "../../../../../scenes/stack/environment/entitiesTable/fieldUtils";
import IntegrationRowFilter from "../../../../posture/steps/components/IntegrationRowFilter";
import { IntegrationsMapping } from "../../../integrations/IntegrationsMapping";

const EntityRawDataView = ({ entityData }) => {
  const colors = useColors();
  const [appliedFilters, setAppliedFilters] = useState({
    search: "",
  });
  const [sourceFilter, setSourceFilter] = useState({ source_integration: {} });

  const flattenedData = flattenEntityData(entityData);
  const sourceOptions = buildSourceOptions(entityData?.integrations);
  const selectedSource = resolveSelectedSource(sourceFilter.source_integration);
  const filteredData = filterData(
    flattenedData,
    appliedFilters.search,
    selectedSource
  );

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={2}
      sx={{
        height: "calc(100vh - 200px)",
        maxHeight: "700px",
        minHeight: "400px",
      }}
    >
      <Box display="flex" gap={1}>
        <SearchInput
          fieldName="search"
          appliedFilters={appliedFilters}
          setAppliedFilters={setAppliedFilters}
          placeholder="Search by key or value..."
        />
        {sourceOptions.length > 0 && (
          <SearchFilter
            filterName={"Source"}
            fieldName={"source_integration"}
            options={sourceOptions}
            appliedFilters={sourceFilter}
            setAppliedFilters={setSourceFilter}
          />
        )}
      </Box>
      <Box
        sx={{
          overflowY: "auto",
          overflowX: "hidden",
          paddingRight: 1,
          scrollbarWidth: "thin",
          "&::-webkit-scrollbar": {
            width: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: colors.text_secondary,
            borderRadius: "2px",
          },
        }}
      >
        {filteredData.length === 0 ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
          >
            <Typography variant="body2" color={colors.text_secondary}>
              No results found
            </Typography>
          </Box>
        ) : (
          filteredData.map((item, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                padding: 1.5,
                backgroundColor: colors.background_paper,
                borderRadius: "8px",
                border: `1px solid ${colors.divider}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontFamily: "monospace",
                  color: colors.primary_main,
                  fontWeight: 500,
                  wordBreak: "break-all",
                }}
              >
                {item.path}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "monospace",
                  color:
                    !item.value ||
                    item.value === "null" ||
                    item.value === "undefined" ||
                    item.value === "{}" ||
                    item.value === "[]"
                      ? colors.text_secondary
                      : colors.text_primary,
                  wordBreak: "break-word",
                  whiteSpace: "pre-wrap",
                  fontStyle:
                    !item.value ||
                    item.value === "null" ||
                    item.value === "undefined" ||
                    item.value === "{}" ||
                    item.value === "[]"
                      ? "italic"
                      : "normal",
                }}
              >
                {!item.value ||
                item.value === "null" ||
                item.value === "undefined" ||
                item.value === "{}" ||
                item.value === "[]"
                  ? "Empty Value"
                  : item.value}
              </Typography>
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
};

const flattenEntityData = (entityData) => {
  /*
  Flattens the entity data into a list of objects like this:
  {
    path: string,
    value: string,
    source: string
  }
  */
  if (!entityData || typeof entityData !== "object") {
    return [];
  }

  const flattenValue = (value, path, source) => {
    if (value === null || value === undefined) {
      return [{ path, value: String(value), source }];
    }

    if (typeof value !== "object") {
      return [{ path, value: String(value), source }];
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        return [{ path, value: "[]", source }];
      }

      return value.flatMap((item, index) =>
        flattenValue(item, `${path}[${index}]`, source)
      );
    }

    const keys = Object.keys(value);
    if (keys.length === 0) {
      return [{ path, value: "{}", source }];
    }

    return keys.flatMap((key) =>
      flattenValue(value[key], `${path}.${key}`, source)
    );
  };

  const rawFields = Object.entries(entityData).filter(([key]) =>
    isRawDataField(key)
  );

  return rawFields.flatMap(([key, value]) => {
    const [source, ...rest] = key.split(RAW_DATA_DELIMITER);
    const fieldPath = rest.join(RAW_DATA_DELIMITER);
    const basePath = [source, fieldPath].filter(Boolean).join(".");

    return flattenValue(value, basePath, source || null);
  });
};

const buildSourceOptions = (integrations = []) =>
  integrations
    .filter((source) => IntegrationsMapping[source])
    .sort()
    .map((source) => ({
      name: source,
      displayName: IntegrationsMapping[source].displayName,
      label: <IntegrationRowFilter name={source} />,
    }));

const resolveSelectedSource = (integrationFilters = {}) =>
  Object.keys(integrationFilters).find((key) => integrationFilters[key]) || "";

const filterData = (flattenedData, searchTerm, selectedSource) => {
  let data = flattenedData;

  if (selectedSource) {
    data = data.filter((item) => item.path.startsWith(selectedSource));
  }

  if (searchTerm && searchTerm.trim()) {
    const lowerSearch = searchTerm.toLowerCase();
    data = data.filter(
      (item) =>
        item.path.toLowerCase().includes(lowerSearch) ||
        String(item.value).toLowerCase().includes(lowerSearch)
    );
  }

  return data;
};

export default EntityRawDataView;
